import React from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { ArrowLeftIcon, ChevronRight } from 'lucide-react';

import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarSeparator,
  useSidebar,
} from '@/components/ui/sidebar';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  ContextSidebarConfig,
  SidebarLink,
  SidebarLinkGroup,
} from '../SidebarContext';
import i18n from '@/utils/i18n';
import { FcEnteringHeavenAlive } from 'react-icons/fc';
import SvgIcon from '../Icon/SvgIcon';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { Button } from '../ui/button';
import { cn } from '@/lib/utils';

const LinkContent: React.FC<{
  link: SidebarLink;
  showDefaultIcon?: boolean;
  labelClassName?: string;
}> = ({ link, showDefaultIcon = true, labelClassName }) => (
  <>
    {link.iconName && <SvgIcon name={link.iconName} className="tw:size-4" />}
    {link.icon && link.icon}
    {showDefaultIcon && !link.icon && !link.iconName && (
      <FcEnteringHeavenAlive className="tw:size-4" />
    )}
    <span className={labelClassName}>{link.label}</span>
    {link.badge && <SidebarMenuBadge>{link.badge}</SidebarMenuBadge>}
  </>
);

interface ContextSidebarProps {
  config: ContextSidebarConfig;
}

const normalizePath = (path: string) => {
  const trimmed = path.split('?')[0].replace(/\/+$/, '');
  return trimmed || '/';
};

const isPathPrefixMatch = (pathname: string, href: string) => {
  if (href === '/') {
    return pathname === '/';
  }

  return pathname === href || pathname.startsWith(`${href}/`);
};

const useBestMatchingHref = ({
  location,
  group,
}: {
  location: ReturnType<typeof useLocation>;
  group: SidebarLinkGroup;
}) =>
  React.useMemo(() => {
    if ((group.isActiveMatchingMethod ?? 'path') !== 'path') {
      return null;
    }

    const normalizedPathname = normalizePath(location.pathname);
    let bestMatchHref: string | null = null;

    for (const link of group.links) {
      if (link.isExternal) {
        continue;
      }

      const normalizedHref = normalizePath(link.href);
      if (!isPathPrefixMatch(normalizedPathname, normalizedHref)) {
        continue;
      }

      if (!bestMatchHref || normalizedHref.length > bestMatchHref.length) {
        bestMatchHref = normalizedHref;
      }
    }

    return bestMatchHref;
  }, [group, location.pathname]);

const getIsActive = ({
  location,
  link,
  isActiveMatchingMethod,
  bestMatchingHref,
}: {
  location: ReturnType<typeof useLocation>;
  link: SidebarLink;
  isActiveMatchingMethod: SidebarLinkGroup['isActiveMatchingMethod'];
  bestMatchingHref: string | null;
}) => {
  if (link.isActive) {
    return true;
  }

  const method = isActiveMatchingMethod ?? 'path';

  if (method === 'path') {
    if (!bestMatchingHref) {
      return false;
    }
    return normalizePath(link.href) === bestMatchingHref;
  }

  if (!!location.search && method === 'searchParam') {
    return link.href.includes(location.search);
  }

  return false;
};

const useIsLinkActive = ({
  location,
  link,
  isActiveMatchingMethod,
  bestMatchingHref,
}: {
  location: ReturnType<typeof useLocation>;
  link: SidebarLink;
  isActiveMatchingMethod: SidebarLinkGroup['isActiveMatchingMethod'];
  bestMatchingHref: string | null;
}) =>
  React.useMemo(
    () =>
      getIsActive({
        location,
        link,
        isActiveMatchingMethod,
        bestMatchingHref,
      }),
    [bestMatchingHref, isActiveMatchingMethod, link, location],
  );

const handleLinkClick = (
  link: SidebarLink,
  options: {
    history: ReturnType<typeof useHistory>;
    isMobile: boolean;
    setOpenMobile: (open: boolean) => void;
  },
) => {
  const { history, isMobile, setOpenMobile } = options;

  if (isMobile) {
    setOpenMobile(false);
  }

  if (link.isExternal) {
    window.open(link.href, '_blank', 'noopener,noreferrer');
    return;
  }

  history.push(link.href);
};

interface CollapsibleGroupProps {
  group: SidebarLinkGroup;
  history: ReturnType<typeof useHistory>;
  location: ReturnType<typeof useLocation>;
  isMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
}

const CollapsibleGroup: React.FC<CollapsibleGroupProps> = ({
  group,
  history,
  location,
  isMobile,
  setOpenMobile,
  sidebarOpen,
  setSidebarOpen,
}) => {
  const [isOpen, setIsOpen] = React.useState(true);
  const bestMatchingHref = useBestMatchingHref({ location, group });

  // If no label, render as flat items (no collapsible wrapper)
  if (!group.label) {
    return (
      <>
        {group.links.map((link) => {
          const isActive = getIsActive({
            location,
            link,
            isActiveMatchingMethod: group.isActiveMatchingMethod,
            bestMatchingHref,
          });

          const isTruncated = link.label.length > 24;

          return (
            <Tooltip delayDuration={isTruncated ? 200 : 999999} key={link.href}>
              <TooltipTrigger asChild>
                <SidebarMenuItem>
                  {link.isExternal || link.disabled || group.disabled ? (
                    <SidebarMenuButton
                      isActive={isActive}
                      className="tw:cursor-pointer"
                      disabled={link.disabled || group.disabled}
                      tooltip={link.label}
                      onClick={() =>
                        handleLinkClick(link, {
                          history,
                          isMobile,
                          setOpenMobile,
                        })
                      }
                    >
                      <LinkContent
                        link={link}
                        labelClassName="tw:flex-1 tw:text-left"
                      />
                    </SidebarMenuButton>
                  ) : (
                    <SidebarMenuButton
                      asChild
                      isActive={isActive}
                      className="tw:cursor-pointer"
                      tooltip={link.label}
                    >
                      <Link
                        to={link.href}
                        className="tw:no-underline!"
                        onClick={() => {
                          if (isMobile) setOpenMobile(false);
                        }}
                      >
                        <LinkContent
                          link={link}
                          labelClassName="tw:flex-1 tw:text-left"
                        />
                      </Link>
                    </SidebarMenuButton>
                  )}
                </SidebarMenuItem>
              </TooltipTrigger>
              <TooltipContent side="right">{link.label}</TooltipContent>
            </Tooltip>
          );
        })}
      </>
    );
  }

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className="tw:group/collapsible"
    >
      <SidebarMenuItem>
        <CollapsibleTrigger
          asChild
          onClick={(e) => {
            if (!sidebarOpen) {
              e.preventDefault();
              setSidebarOpen(true);
            }
          }}
        >
          <SidebarMenuButton
            tooltip={group.label}
            className="tw:cursor-pointer"
          >
            <span>{group.label}</span>
            <ChevronRight className="tw:ml-auto tw:transition-transform tw:duration-200 tw:group-data-[state=open]/collapsible:rotate-90" />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            {group.links.map((link) => {
              const isActive = getIsActive({
                location,
                link,
                isActiveMatchingMethod: group.isActiveMatchingMethod,
                bestMatchingHref,
              });

              const isTruncated = link.label.length > 24;

              return (
                <Tooltip
                  delayDuration={isTruncated ? 200 : 999999}
                  key={link.href}
                >
                  <TooltipTrigger asChild>
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton asChild isActive={isActive}>
                        {link.isExternal ? (
                          <a
                            href={link.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="tw:no-underline!"
                          >
                            <LinkContent link={link} showDefaultIcon={false} />
                          </a>
                        ) : (
                          <Link
                            to={link.href}
                            onClick={() => {
                              if (isMobile) setOpenMobile(false);
                            }}
                            className="tw:no-underline!"
                          >
                            <LinkContent link={link} showDefaultIcon={false} />
                          </Link>
                        )}
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  </TooltipTrigger>
                  <TooltipContent side="right">{link.label}</TooltipContent>
                </Tooltip>
              );
            })}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
};

interface GroupedLinkItemProps {
  location: ReturnType<typeof useLocation>;
  link: SidebarLink;
  group: SidebarLinkGroup;
  history: ReturnType<typeof useHistory>;
  isMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  bestMatchingHref: string | null;
}

const GroupedLinkItem: React.FC<GroupedLinkItemProps> = ({
  location,
  link,
  group,
  history,
  isMobile,
  setOpenMobile,
  bestMatchingHref,
}) => {
  const isActive = useIsLinkActive({
    location,
    link,
    isActiveMatchingMethod: group.isActiveMatchingMethod,
    bestMatchingHref,
  });
  const isTruncated = link.label.length > 24;

  return (
    <Tooltip delayDuration={isTruncated ? 200 : 999999} key={link.href}>
      <TooltipTrigger asChild>
        <SidebarMenuItem>
          {link.isExternal || link.disabled || group.disabled ? (
            <SidebarMenuButton
              isActive={isActive}
              className="tw:cursor-pointer"
              disabled={link.disabled || group.disabled}
              tooltip={link.label}
              onClick={() =>
                handleLinkClick(link, {
                  history,
                  isMobile,
                  setOpenMobile,
                })
              }
            >
              <LinkContent
                link={link}
                labelClassName="tw:flex-1 tw:text-left tw:capitalize"
              />
            </SidebarMenuButton>
          ) : (
            <SidebarMenuButton
              asChild
              isActive={isActive}
              className="tw:cursor-pointer"
              tooltip={link.label}
            >
              <Link
                to={link.href}
                className="tw:no-underline!"
                onClick={() => {
                  if (isMobile) setOpenMobile(false);
                }}
              >
                <LinkContent
                  link={link}
                  labelClassName="tw:flex-1 tw:text-left tw:capitalize"
                />
              </Link>
            </SidebarMenuButton>
          )}
        </SidebarMenuItem>
      </TooltipTrigger>
      <TooltipContent side="right">{link.label}</TooltipContent>
    </Tooltip>
  );
};

interface GroupedLinksProps {
  location: ReturnType<typeof useLocation>;
  group: SidebarLinkGroup;
  history: ReturnType<typeof useHistory>;
  isMobile: boolean;
  setOpenMobile: (open: boolean) => void;
}

const GroupedLinks: React.FC<GroupedLinksProps> = ({
  location,
  group,
  history,
  isMobile,
  setOpenMobile,
}) => {
  const bestMatchingHref = useBestMatchingHref({ location, group });

  return (
    <>
      {group.links.map((link) => (
        <GroupedLinkItem
          key={link.href}
          location={location}
          link={link}
          group={group}
          history={history}
          isMobile={isMobile}
          setOpenMobile={setOpenMobile}
          bestMatchingHref={bestMatchingHref}
        />
      ))}
    </>
  );
};

export const ContextSidebar: React.FC<ContextSidebarProps> = ({ config }) => {
  const history = useHistory();
  const location = useLocation();
  const { isMobile, setOpenMobile, open, setOpen } = useSidebar();

  const mode = config.mode ?? 'collapsible';

  const handleBackClick = () => {
    const back = config.back;
    if (!back) return;

    if (back.onClick) {
      back.onClick();
      return;
    }

    if (back.to) {
      if (isMobile) {
        setOpenMobile(false);
      }
      history.push(back.to);
      return;
    }

    history.goBack();
  };

  const renderBackButton = () => {
    if (!config?.back) return null;

    const backLabel = config.back.label ?? i18n.t('Common:back') ?? 'Back';
    const isCollapsed = !isMobile && !open;
    const button = (
      <Button
        variant="secondary"
        size={isCollapsed ? 'icon-sm' : 'default'}
        onClick={handleBackClick}
        aria-label={backLabel}
        className={cn(
          'tw:transition-[width,height,padding] tw:duration-200',
          isCollapsed ? 'tw:mx-auto' : 'tw:w-full',
        )}
      >
        <ArrowLeftIcon />
        {!isCollapsed && <span className="tw:truncate">{backLabel}</span>}
      </Button>
    );

    return (
      <div className="tw:w-full">
        <div
          className={cn(
            'tw:w-full tw:transition-[padding] tw:duration-200',
            isCollapsed ? 'tw:px-2 tw:py-4' : 'tw:p-4',
          )}
        >
          {isCollapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>{button}</TooltipTrigger>
              <TooltipContent side="right">{backLabel}</TooltipContent>
            </Tooltip>
          ) : (
            button
          )}
        </div>
        <SidebarSeparator className="tw:mx-0" />
      </div>
    );
  };

  const renderHeader = () =>
    config.header ? (
      <div className="tw:px-2 tw:pt-2">{config.header}</div>
    ) : null;

  if (mode === 'collapsible') {
    return (
      <>
        {renderBackButton()}
        {renderHeader()}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu className="tw:gap-1">
              {config.groups.map((group, groupIndex) => (
                <CollapsibleGroup
                  key={group.label ?? groupIndex}
                  group={group}
                  history={history}
                  location={location}
                  isMobile={isMobile}
                  setOpenMobile={setOpenMobile}
                  sidebarOpen={open}
                  setSidebarOpen={setOpen}
                />
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </>
    );
  }

  // Grouped mode
  return (
    <>
      {renderBackButton()}
      {renderHeader()}
      {config.groups.map((group, groupIndex) => (
        <SidebarGroup key={group.label ?? groupIndex}>
          {group.label && (
            <SidebarGroupLabel className="tw:text-xs tw:font-medium tw:text-primary/50">
              {group.label}
            </SidebarGroupLabel>
          )}
          <SidebarGroupContent>
            <SidebarMenu>
              <GroupedLinks
                location={location}
                group={group}
                history={history}
                isMobile={isMobile}
                setOpenMobile={setOpenMobile}
              />
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      ))}
    </>
  );
};
